import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import StatusBadge from "../ui/StatusBadge.jsx";
import AppButton from "../ui/AppButton.jsx";
import { emojiForProjektTyp, labelForProjektTyp } from "../../constants/projektTypVisuals.js";
import { filterRelevantMessprotokollHubRows } from "../../utils/objektMessprotokollFilter.js";

function protocolStatusLabel(row) {
  if (row.has_protocol) return { text: "Vorhanden", cls: "text-emerald-400" };
  if (!row.expects_protocol) return { text: "Nicht erforderlich", cls: "text-blue-200/50" };
  if (row.warning_rules?.length) return { text: "Fehlt", cls: "text-amber-400 font-medium" };
  return { text: "Offen", cls: "text-blue-200/70" };
}

export default function ObjektMessprotokolleList({ objektId, onStatsChange = null }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!objektId) return;
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/objekte/${objektId}/messprotokolle`);
      const all = Array.isArray(data) ? data : [];
      const visible = filterRelevantMessprotokollHubRows(all);
      setRows(visible);
      onStatsChange?.({
        total: all.length,
        visible: visible.length,
        missing: visible.filter((r) => r.expects_protocol && !r.has_protocol).length,
      });
    } catch {
      setRows([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [objektId, onStatsChange]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-blue-200/60 py-2">Lade Messprotokolle…</p>;
  }
  if (error) {
    return <p className="text-sm text-amber-300/90 py-2">Messprotokoll-Übersicht konnte nicht geladen werden.</p>;
  }
  if (!rows.length) {
    return (
      <p className="text-sm text-blue-200/70 py-2">
        Keine Messprotokolle an diesem Objekt — es sind keine Projekte mit erwartetem oder vorhandenem Protokoll verknüpft.
      </p>
    );
  }

  const missing = rows.filter((r) => r.expects_protocol && !r.has_protocol);

  return (
    <div className="space-y-3 min-w-0">
      {missing.length > 0 ? (
        <p className="text-xs text-amber-300/90">
          {missing.length} Projekt{missing.length === 1 ? "" : "e"} mit erwartetem, fehlendem Messprotokoll.
        </p>
      ) : null}
      <div className="overflow-x-auto -mx-1">
        <table className="w-full min-w-[520px] text-sm text-left border-collapse">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-blue-200/50 border-b border-white/10">
              <th className="py-2 px-2 font-medium">Projekt</th>
              <th className="py-2 px-2 font-medium">Art</th>
              <th className="py-2 px-2 font-medium">Status</th>
              <th className="py-2 px-2 font-medium">Protokoll</th>
              <th className="py-2 px-2 font-medium w-24" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const proto = protocolStatusLabel(row);
              const typ = String(row.typ || "").toLowerCase();
              return (
                <tr
                  key={`${typ}-${row.id}`}
                  className={`border-b border-white/5 ${row.archiviert ? "opacity-60" : ""}`}
                >
                  <td className="py-2.5 px-2 text-blue-100/90 max-w-[200px] truncate">
                    {row.name || labelForProjektTyp(typ)}
                  </td>
                  <td className="py-2.5 px-2 text-blue-200/75 whitespace-nowrap">
                    {emojiForProjektTyp(typ)} {labelForProjektTyp(typ)}
                  </td>
                  <td className="py-2.5 px-2">
                    {row.status ? <StatusBadge status={row.status} className="!text-[10px]" /> : "–"}
                  </td>
                  <td className={`py-2.5 px-2 ${proto.cls}`}>{proto.text}</td>
                  <td className="py-2.5 px-2 text-right">
                    <AppButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(row.projekt_path)}
                    >
                      Öffnen
                    </AppButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
